import React, { useState, useEffect, useRef, useCallback } from "react";
import { useApi } from "../../context/apiContext";
import {
  Column,
  Table,
  AutoSizer,
  SortDirection,
  ColumnSizer,
} from "react-virtualized";
import "react-virtualized/styles.css";
import { format } from "date-fns";
import {
  FaFileDownload,
  FaChevronUp,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import FunnelFilter from "./FunnelFilter";
import Description from "./Description";
import SideDrawer from "../Download/SideDrawer";
import { getSasUrl } from "../../services/sasService";
import {
  DOWNLOAD_INSTRUCTIONS_BLOB_PATH,
  DOWNLOAD_INSTRUCTIONS_SAS_MINUTES,
  LARGE_FILE_DRAWER_THRESHOLD_BYTES,
  sasDurationMinutesForFileSize,
} from "../../utils/downloadConstants";

const PAGE_SIZE = 50;
const MAX_PATH_SEGMENT_PREFIX = 14;

/**
 * Show storage paths from the rbfs segment onward (never the lakehouse URL).
 * Example: rbfs/phase2-data/east
 */
const getRbfsRelativePath = (parentPath) => {
  if (!parentPath) return "";

  let normalizedPath = String(parentPath)
    .replace(/\\/g, "/")
    .trim()
    // Collapse spaces around slashes from sync artifacts: "phase2-data /east"
    .replace(/\s*\/\s*/g, "/");

  const rbfsMatch = /(?:^|\/)(rbfs)(?:\/|$)/i.exec(normalizedPath);
  if (rbfsMatch) {
    return normalizedPath.slice(rbfsMatch.index).replace(/^\/+/, "");
  }

  // Older records may store a full blob URL — strip host + container.
  try {
    const url = new URL(normalizedPath);
    const parts = url.pathname.split("/").filter(Boolean);
    const rbfsIdx = parts.findIndex((p) => p.toLowerCase() === "rbfs");
    if (rbfsIdx >= 0) {
      return parts.slice(rbfsIdx).join("/");
    }
    const afterContainer = parts.length > 1 ? parts.slice(1) : parts;
    return afterContainer.join("/");
  } catch {
    return normalizedPath.replace(/^\/+/, "");
  }
};

/**
 * Middle-style path display: keep short segments; long ones become "prefix...".
 * Example: rbfs/phase2-data/east/east-region-fl.../28725502-4c.../
 */
const truncatePath = (path) => {
  if (!path) return "";

  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return "";

  const truncated = segments.map((segment) =>
    segment.length > MAX_PATH_SEGMENT_PREFIX
      ? `${segment.slice(0, MAX_PATH_SEGMENT_PREFIX)}...`
      : segment,
  );

  return `${truncated.join("/")}/`;
};

const getPageNumbers = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) pages.push("start-ellipsis");
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < totalPages - 1) pages.push("end-ellipsis");
  pages.push(totalPages);

  return pages;
};

const FinderComponent = ({ token }) => {
  const [files, setFiles] = useState([]);
  const [path, setPath] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const { apiType, apiClient } = useApi();
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ type: "" });
  const [sortBy, setSortBy] = useState([
    { field: "type", direction: SortDirection.DESC },
    { field: "parent_path_length", direction: SortDirection.ASC },
    { field: "name", direction: SortDirection.ASC },
  ]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalRecords, setTotalRecords] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filterByKind, setFilterByKind] = useState("All");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [sizeInGB, setSizeInGB] = useState(0);
  const [instructionUrl, setInstructionUrl] = useState("");
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);
  const [isResultsExpanded, setIsResultsExpanded] = useState(true);
  const [downloadInFlight, setDownloadInFlight] = useState(false);
  const downloadInFlightRef = useRef(false);

  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);
  const tableRef = useRef(null);
  const ROW_HEIGHTS = {
    COMPACT: 52,
    DEFAULT: 64,
    WITH_PATH: 88,
  };

  const getDisplayParentPath = (rowData) =>
    getRbfsRelativePath(rowData.parentPath);

  // Keep the API path intact for navigation, but do not show placeholder
  // segments such as "-" in the breadcrumb. Paths start at rbfs (no lakehouse URL).
  const visibleBreadcrumbs = breadcrumbs
    .map((crumb, index) => ({ crumb, index }))
    .filter(({ crumb }) => crumb && crumb !== "-");

  const rbfsBreadcrumbStart = visibleBreadcrumbs.findIndex(({ crumb }) =>
    /^rbfs$/i.test(crumb),
  );
  const displayBreadcrumbs =
    rbfsBreadcrumbStart >= 0
      ? visibleBreadcrumbs.slice(rbfsBreadcrumbStart)
      : visibleBreadcrumbs;

  const getRowHeight = ({ index }) => {
    const rowData = files[index] || {};
    const hasLongName = rowData.name && rowData.name.length > 50;
    const hasDisplayParentPath = Boolean(getDisplayParentPath(rowData));

    if (hasLongName || hasDisplayParentPath) {
      return ROW_HEIGHTS.WITH_PATH;
    }

    return window.innerWidth <= 768 ? ROW_HEIGHTS.COMPACT : ROW_HEIGHTS.DEFAULT;
  };

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.recomputeRowHeights();
      tableRef.current.forceUpdateGrid();
    }
  }, [files, isDescriptionExpanded, isResultsExpanded]);

  const handleFilterChange = (selectedKind) => {
    if (selectedKind === "All") {
      setFilters({ type: "" });
    } else {
      setFilters({ type: selectedKind.toLowerCase() });
    }
    setFilterByKind(selectedKind);
  };

  const toggleDescription = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const toggleResults = () => {
    setIsResultsExpanded(!isResultsExpanded);
  };

  const fetchPage = useCallback(
    async (pageNumber) => {
      const requestId = ++requestIdRef.current;
      loadingRef.current = true;
      setIsLoading(true);
      const offset = (pageNumber - 1) * PAGE_SIZE;
      try {
        const requestData = {
          search: searchQuery,
          type: filters.type,
          parent_path: path,
          sort_by: sortBy.map((sort) => sort.field).join(","),
          sort_direction: sortBy.map((sort) => sort.direction).join(","),
          offset,
          limit: PAGE_SIZE,
        };
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [response, countResponse] = await Promise.all([
          apiClient.postRequest("/files/search", requestData, apiType, headers),
          apiClient
            .postRequest("/files/search/count", requestData, apiType, headers)
            .catch(() => null),
        ]);
        if (requestId !== requestIdRef.current) return;

        const rows = Array.isArray(response) ? response : [];
        const total = Number(countResponse?.total);
        const hasTotal = Number.isFinite(total) && total >= 0;
        setFiles(rows);
        setTotalRecords(hasTotal ? total : null);
        setHasMore(
          hasTotal ? pageNumber * PAGE_SIZE < total : rows.length === PAGE_SIZE,
        );
      } catch (error) {
        if (requestId === requestIdRef.current) {
          console.error("Error fetching files:", error);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          loadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [searchQuery, path, sortBy, filters, token, apiClient, apiType],
  );

  // Reset to page 1 whenever search criteria change.
  useEffect(() => {
    setPage(1);
    setTotalRecords(null);
  }, [searchQuery, path, filters, sortBy]);

  useEffect(() => {
    fetchPage(page);
  }, [fetchPage, page]);
  const handleSearch = (query) => {
    setBreadcrumbs([]);
    setPath(null);
    setSearchQuery(query);
  };

  const goToPage = (nextPage) => {
    if (
      isLoading ||
      nextPage === page ||
      nextPage < 1 ||
      nextPage > totalPages
    ) {
      return;
    }
    if (nextPage > page && !hasMore) return;
    setPage(nextPage);
  };

  const canGoPrev = page > 1 && !isLoading;
  const totalPages =
    totalRecords === null
      ? hasMore
        ? page + 1
        : page
      : Math.ceil(totalRecords / PAGE_SIZE);
  const canGoNext = hasMore && page < totalPages && !isLoading;
  const rangeStart = files.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = (page - 1) * PAGE_SIZE + files.length;
  const pageNumbers =
    totalRecords === null ? [] : getPageNumbers(page, totalPages);

  const rowGetter = ({ index }) => {
    return files[index] || {};
  };

  const handleRowClick = (rowData) => {
    if (rowData.type !== "folder") return;

    // Prefer the stored path so spaced DB paths (e.g. "/phase2-data /east") match.
    const nextPath =
      rowData.path ||
      [rowData.parentPath, rowData.name].filter(Boolean).join("/");
    if (!nextPath) return;

    setPath(nextPath);
    setBreadcrumbs(nextPath.split("/"));
    setSearchText("");
    setSearchQuery("");
  };

  const handleBreadcrumbClick = (index) => {
    if (index == 0) return;
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setPath(newBreadcrumbs.join("/"));
    setSearchText("");
    setSearchQuery("");
  };

  const handleSort = (dataKey) => {
    const updatedSort = sortBy.find((sort) => sort.field === dataKey)
      ? sortBy.map((sort) =>
          sort.field === dataKey
            ? {
                ...sort,
                direction:
                  sort.direction === SortDirection.ASC
                    ? SortDirection.DESC
                    : SortDirection.ASC,
              }
            : sort,
        )
      : [...sortBy, { field: dataKey, direction: SortDirection.ASC }];

    setSortBy(updatedSort);
  };

  const headerRenderer = ({ dataKey, label }) => {
    const sortInfo = sortBy.find((sort) => sort.field === dataKey);
    const direction = sortInfo ? sortInfo.direction : null;

    return (
      <div className="dataset-header">
        <button
          type="button"
          className="dataset-header-label"
          onClick={() => handleSort(dataKey)}
        >
          <span>{label}</span>
          {direction && (
            <span className="sort-icon">
              {direction === SortDirection.ASC ? "▲" : "▼"}
            </span>
          )}
        </button>

        {dataKey === "type" && (
          <FunnelFilter
            options={["All", "Folder", "File"]}
            selectedOption={filterByKind}
            onFilterChange={handleFilterChange}
          />
        )}
      </div>
    );
  };

  const nameCellRenderer = ({ cellData, rowData }) => {
    const displayParentPath = getDisplayParentPath(rowData);
    const truncatedParentPath = truncatePath(displayParentPath);

    return (
      <div className="dataset-name-cell">
        <div className="dataset-cell" title={cellData}>
          {cellData}
        </div>
        {displayParentPath && (
          <div className="dataset-footnote" title={displayParentPath}>
            {truncatedParentPath}
          </div>
        )}
      </div>
    );
  };

  const downloadFile = async (rowData) => {
    if (downloadInFlightRef.current) return;
    downloadInFlightRef.current = true;
    setDownloadInFlight(true);

    const fileName = rowData.name;
    const sizeInBytes = rowData.fileMetadata?.size ?? 0;
    const sizeInGB = sizeInBytes / 1024 ** 3;
    const durationMinutes = sasDurationMinutesForFileSize(sizeInBytes);
    try {
      const fileUrl = await getSasUrl(rowData.path, {
        permissions: "r",
        durationMinutes,
      });
      if (sizeInBytes > LARGE_FILE_DRAWER_THRESHOLD_BYTES) {
        let nextInstructionUrl = "";
        try {
          nextInstructionUrl = await getSasUrl(
            DOWNLOAD_INSTRUCTIONS_BLOB_PATH,
            {
              permissions: "r",
              durationMinutes: DOWNLOAD_INSTRUCTIONS_SAS_MINUTES,
            },
          );
        } catch (instructionErr) {
          console.warn(
            "Could not mint SAS for download instructions PDF:",
            instructionErr,
          );
        }
        setFileName(fileName);
        setFileUrl(fileUrl);
        setSizeInGB(sizeInGB);
        setInstructionUrl(nextInstructionUrl);
        setIsDrawerOpen(true);
      } else {
        handleBrowserDownload(fileUrl, fileName);
      }
    } catch (err) {
      console.error("Failed to mint SAS URL for download:", err);
      alert(
        err.message ||
          "Unable to prepare download. Please sign in and try again.",
      );
    } finally {
      downloadInFlightRef.current = false;
      setDownloadInFlight(false);
    }
  };

  const typeCellRenderer = ({ rowData }) => {
    if (rowData.type === "file") {
      return (
        <button
          type="button"
          className="dataset-file-cell"
          onClick={() => downloadFile(rowData)}
          disabled={downloadInFlight}
          aria-busy={downloadInFlight}
          aria-label={`Download ${rowData.name}`}
        >
          <span>File</span>
          <FaFileDownload className="filedownload-icon" />
        </button>
      );
    }
    return <span>Folder</span>;
  };

  const handleBrowserDownload = (fileUrl, fileName) => {
    const anchor = document.createElement("a");
    anchor.target = "_parent";

    // Parse URL to handle query strings
    const url = new URL(fileUrl);
    const pathParts = url.pathname.split("/");
    const encodedFileName = encodeURIComponent(fileName);

    // Replace the last part of pathname with encoded filename
    pathParts[pathParts.length - 1] = encodedFileName;
    url.pathname = pathParts.join("/");

    anchor.href = url.toString();
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setIsDrawerOpen(false);
  };

  const formatSize = (size) => {
    if (!size) return "-";
    const units = ["B", "KB", "MB", "GB"];
    const index = Math.floor(Math.log(size) / Math.log(1024));
    return `${(size / Math.pow(1024, index)).toFixed(2)} ${units[index]}`;
  };

  const formatDate = (dateString) => {
    return dateString
      ? format(new Date(dateString), "MMM dd, yyyy HH:mm")
      : "-";
  };

  return (
    /* fixed this accessibility issue,make the clickable div element keyboard-accessible by:
          Adding a tabIndex prop to make it focusable
          Adding an onKeyDown handler for keyboard interaction
          Using the semantically appropriate ARIA role
          Adding appropriate ARIA labels */
    <div className="search-container-1">
      <div className="content-card finder-content finder-shell">
        <div className="content-card finder-description-card">
          <button
            type="button"
            className="card-header"
            onClick={toggleDescription}
            aria-expanded={isDescriptionExpanded}
            aria-controls="description-section"
            aria-label={
              isDescriptionExpanded
                ? "Collapse description"
                : "Expand description"
            }
          >
            <span className="card-title">Description</span>
            <span className="accordion-controls" aria-hidden="true">
              <span
                className={`toggle-btn ${
                  isDescriptionExpanded ? "expanded" : "collapsed"
                }`}
              >
                {isDescriptionExpanded ? (
                  <FaChevronUp className="up-rotate" />
                ) : (
                  <FaChevronDown className="down-rotate" />
                )}
              </span>
            </span>
          </button>
          <div
            id="description-section"
            className={`card-body ${
              isDescriptionExpanded ? "expanded" : "collapsed"
            }`}
          >
            <Description />
          </div>
        </div>
        {/* Results Card */}
        <div className="content-card finder-datasets-card">
          <button
            type="button"
            className="card-header"
            onClick={toggleResults}
            aria-expanded={isResultsExpanded}
            aria-controls="datasets-section"
            aria-label={
              isResultsExpanded ? "Collapse results" : "Expand results"
            }
          >
            <span className="card-title">Datasets</span>
            <span className="accordion-controls" aria-hidden="true">
              <span
                className={`toggle-btn ${
                  isResultsExpanded ? "expanded" : "collapsed"
                }`}
              >
                {isResultsExpanded ? (
                  <FaChevronUp className="up-rotate" />
                ) : (
                  <FaChevronDown className="down-rotate" />
                )}
              </span>
            </span>
          </button>
          <div
            id="datasets-section"
            className={`card-body ${
              isResultsExpanded ? "expanded" : "collapsed"
            }`}
          >
            <div className="search-bar">
              <input
                type="text"
                placeholder="Type a keyword and click enter..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch(searchText)}
                className="dataset-search-input"
              />
            </div>
            <div className="dataset-table-container">
              <div className="dataset-table-viewport">
                <AutoSizer>
                  {({ height, width }) => (
                    <ColumnSizer columnCount={4} width={width}>
                      {({ adjustedWidth }) => {
                        const columnWidths = [
                          adjustedWidth * 0.65,
                          adjustedWidth * 0.1,
                          adjustedWidth * 0.1,
                          adjustedWidth * 0.15,
                        ];
                        return (
                          <Table
                            ref={tableRef}
                            key={`finder-table-${isDescriptionExpanded ? "desc-on" : "desc-off"}`}
                            width={width}
                            height={height}
                            headerHeight={52}
                            rowHeight={getRowHeight}
                            rowCount={files.length}
                            rowGetter={rowGetter}
                            onRowClick={({ rowData }) =>
                              handleRowClick(rowData)
                            }
                            sort={({ sortBy }) => handleSort(sortBy)}
                            className="finder-table"
                            rowClassName={({ index }) => {
                              const rowData = files[index] || {};
                              return `${
                                rowData.type === "folder"
                                  ? "folder-row"
                                  : "file-row"
                              } ${rowData.isActive ? "active" : ""}`;
                            }}
                          >
                            <Column
                              label="Name"
                              dataKey="name"
                              width={columnWidths[0]}
                              cellRenderer={nameCellRenderer}
                              headerRenderer={headerRenderer}
                              style={{ paddingLeft: 0 }}
                            />
                            <Column
                              label="Kind"
                              dataKey="type"
                              width={columnWidths[1]}
                              cellRenderer={typeCellRenderer}
                              headerRenderer={headerRenderer}
                              style={{ paddingLeft: 0 }}
                            />
                            <Column
                              label="Size"
                              dataKey="size"
                              width={columnWidths[2]}
                              cellRenderer={({ rowData }) =>
                                rowData.fileMetadata?.size
                                  ? formatSize(rowData.fileMetadata.size)
                                  : "-"
                              }
                              headerRenderer={headerRenderer}
                              style={{ paddingLeft: 0 }}
                            />
                            <Column
                              label="Last Modified"
                              dataKey="last_modified"
                              width={columnWidths[3]}
                              cellRenderer={({ rowData }) =>
                                rowData.fileMetadata?.last_modified
                                  ? formatDate(
                                      rowData.fileMetadata.last_modified,
                                    )
                                  : "-"
                              }
                              headerRenderer={headerRenderer}
                              style={{ paddingLeft: 0 }}
                            />
                          </Table>
                        );
                      }}
                    </ColumnSizer>
                  )}
                </AutoSizer>
              </div>
              <div
                className="finder-pagination"
                aria-label="Dataset pagination"
              >
                <span className="finder-pagination-range">
                  {files.length === 0
                    ? isLoading
                      ? "Loading…"
                      : "No results"
                    : `Showing ${rangeStart}–${rangeEnd}${
                        totalRecords === null ? "" : ` of ${totalRecords}`
                      }`}
                </span>
                <div className="finder-pagination-controls">
                  <button
                    type="button"
                    className="finder-page-btn"
                    onClick={() => goToPage(page - 1)}
                    disabled={!canGoPrev}
                    aria-label="Previous page"
                  >
                    <FaChevronLeft aria-hidden="true" />
                    Prev
                  </button>
                  <div
                    className="finder-page-numbers"
                    aria-label="Page numbers"
                  >
                    {pageNumbers.map((pageNumber) =>
                      typeof pageNumber === "string" ? (
                        <span key={pageNumber} className="finder-page-ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          key={pageNumber}
                          type="button"
                          className={`finder-page-number ${
                            pageNumber === page ? "active" : ""
                          }`}
                          onClick={() => goToPage(pageNumber)}
                          disabled={isLoading || pageNumber === page}
                          aria-current={
                            pageNumber === page ? "page" : undefined
                          }
                        >
                          {pageNumber}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    className="finder-page-btn"
                    onClick={() => goToPage(page + 1)}
                    disabled={!canGoNext}
                    aria-label="Next page"
                  >
                    Next
                    <FaChevronRight aria-hidden="true" />
                  </button>
                </div>
              </div>
              <SideDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                fileName={fileName}
                sizeInGB={sizeInGB}
                fileUrl={fileUrl}
                instructionUrl={instructionUrl}
                resolveInstructionUrl={() =>
                  getSasUrl(DOWNLOAD_INSTRUCTIONS_BLOB_PATH, {
                    permissions: "r",
                    durationMinutes: DOWNLOAD_INSTRUCTIONS_SAS_MINUTES,
                  })
                }
                handleBrowserDownload={() =>
                  handleBrowserDownload(fileUrl, fileName)
                }
              />
            </div>
          </div>
        </div>
      </div>
      {displayBreadcrumbs.length > 0 && (
        <div
          className="breadcrumb"
          title={displayBreadcrumbs.map(({ crumb }) => crumb).join("/")}
        >
          {displayBreadcrumbs.map(({ crumb, index }, visibleIndex) => (
            <React.Fragment key={`path-${index}`}>
              {visibleIndex > 0 && (
                <span className="breadcrumb-separator">/</span>
              )}
              <button
                type="button"
                className="breadcrumb-button"
                onClick={() => handleBreadcrumbClick(index)}
                title={crumb}
              >
                {crumb}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default FinderComponent;
